import { CommissionRecord } from "../../../types/commission";

export interface CommissionHistory {
  /** The record in force today (latest start on or before today). */
  current: CommissionRecord | null;
  /** Records starting after today, soonest first. */
  upcoming: CommissionRecord[];
  /** Records before the current one, newest first, each with the record
   * that superseded it (which ends its effective range). */
  past: { record: CommissionRecord; next: CommissionRecord | null }[];
}

/** Today as YYYY-MM-DD in local time (records store local dates). */
export const todayIso = (now: Date = new Date()): string =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(
    now.getDate(),
  ).padStart(2, "0")}`;

/**
 * Split a staff member's commission records into current / upcoming / past.
 * Effective-dated and append-only: a record runs until the next one starts.
 */
export const splitCommissionHistory = (
  records: CommissionRecord[],
  today: string = todayIso(),
): CommissionHistory => {
  const ascending = [...records].sort((a, b) =>
    a.effective_start_date.localeCompare(b.effective_start_date),
  );
  const started = ascending.filter((r) => r.effective_start_date <= today);
  const current = started.length ? started[started.length - 1] : null;
  const upcoming = ascending.filter((r) => r.effective_start_date > today);
  const past = started
    .slice(0, -1)
    .map((record, i) => ({ record, next: started[i + 1] ?? null }))
    .reverse();
  return { current, upcoming, past };
};
